import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../contexts/auth';
import { getPermissions } from '../services/api_permissions';
import Fetching from './fetching';
import CustomTooltipComponent from './custom_tooltip_component';


const DESCRIPTIONS_BY_CODENAME = {
  // --- HANDLER ---
  'hr.manage_inouts_all': 'Può creare, modificare o eliminare le timbrature per tutti i dipendenti.',
  'hr.view_inouts_all': 'Può visualizzare le timbrature di tutti i dipendenti.',
  'hr.manage_shifts_all': 'Può creare, modificare o eliminare i turni di tutti i dipendenti.',
  'hr.view_shifts_all': 'Può visualizzare i turni di tutti i dipendenti.',
  'hr.manage_working_area_all': 'Può creare e assegnare le aree di lavoro a tutti i dipendenti.',
  'hr.view_presences_working_area_all': 'Può accedere alla pagina "Presenze Aree Lavoro" e visualizzare in tempo reale le presenze in area e timbrature registrate durante la giornata fino al momento della richiesta per tutti i dipendenti.',
  'hr.manage_justification_all': 'Può creare, approvare, rifiutare o eliminare i giustificativi di tutti i dipendenti.',
  'hr.view_attendance_all': 'Può visualizzare il calendario presenze di tutti i dipendenti.',
  'hr.view_docs_all': 'Può visualizzare e caricare i documenti (cedolini, cu, documenti, documenti con firma) di tutti i dipendenti.',
  'hr.manage_refunds_all': 'Può creare, approvare, rifiutare o eliminare le richieste di rimborsi spese di tutti i dipendenti.',
  'hr.manage_alerts_all': 'Può creare, visualizzare o eliminare le comunicazioni verso tutti i dipendenti.',
  'hr.manage_custom_letters_all': 'Può accedere alla funzionalità di generazione lettere e personalizzare i format presenti visualizzando la lista di tutti i dipendenti nella compilazione del format.',
  'hr.manage_inouts_ou': 'Può creare, modificare o eliminare le timbrature dei dipendenti della propria Unità Organizzativa.',
  'hr.view_inouts_ou': 'Può visualizzare le timbrature dei dipendenti della propria Unità Organizzativa.',
  'hr.manage_shifts_ou': 'Può creare, modificare o eliminare i turni dei dipendenti della propria Unità Organizzativa.',
  'hr.view_shifts_ou': 'Può visualizzare i turni dei dipendenti della propria Unità Organizzativa.',
  'hr.manage_working_area_ou': 'Può creare e assegnare le aree di lavoro ai dipendenti della propria unità.',
  'hr.view_presences_working_area_ou': 'Può accedere alla pagina "Presenze Aree Lavoro" e visualizzare in tempo reale le presenze in area e timbrature registrate durante la giornata fino al momento della richiesta dei dipendenti della propria unità.',
  'hr.manage_justification_ou': 'Può creare, approvare, rifiutare o eliminare i giustificativi dei dipendenti della propria Unità Organizzativa.',
  'hr.view_attendance_ou': 'Può visualizzare il calendario presenze dei dipendenti della propria Unità Organizzativa.',
  'hr.view_docs_ou': 'Può visualizzare e caricare i documenti (cedolini, cu, documenti, documenti con firma) dei dipendenti della propria Unità Organizzativa.',
  'hr.manage_refunds_ou': 'Può creare, approvare, rifiutare o eliminare le richieste di rimborsi spese dei dipendenti della propria Unità Organizzativa.',
  'hr.manage_alerts_ou': 'Può creare, visualizzare o eliminare le comunicazioni verso i dipendenti della propria Unità Organizzativa.',
  'hr.manage_custom_letters_ou': 'Può accedere alla funzionalità di generazione lettere e personalizzare i format presenti visualizzando la lista di dei dipendenti appartenenti alla propria unità nella compilazione del format.',
  'hr.manage_shifts': 'Può gestire i propri dati e le varie funzioni relative al proprio profilo. Tale permesso consentirà quindi al dipendente di approvare i propri giustificativi, impostare i propri turni e gestire le proprie richieste di rimborsi spese in qualità di gestore di sé stesso.',
  // --- EMPLOYEE ---
  'hr.view_shifts': 'Può visualizzare i propri turni, avvisi e la sezione rimborsi spese',
  'hr.manage_inouts': 'Può registrare tramite app la propria timbratura in entrata e in uscita. Per abilitare la registrazione è necessario che anche il permesso "Visualizza timbrature" sia attivo',
  'hr.view_inouts': 'Può visualizzare le proprie timbrature.',
  'hr.can_request_justification': 'Può inviare richieste di giustificativo (per i giustificativi abilitati dall\'azienda), come ferie, permessi, etc.',
  'hr.view_docs': 'Può visualizzare i propri documenti (cedolini, CU, ecc.).',
};

const InfoDot = ({ title }) => (
  <CustomTooltipComponent title={title} placement="top-start" arrow>
    <span
      role="button"
      tabIndex={0}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      className="
        ml-2 inline-flex h-4 w-4 items-center justify-center
        rounded-full border border-indigo-200 text-[12px] font-medium
        text-indigo-400 hover:text-indigo-600 hover:border-indigo-400
        focus:outline-none focus:ring-2 focus:ring-indigo-400/30
        transition-colors align-middle select-none bg-indigo-50
      "
      aria-label="Informazioni"
    >
      i
    </span>
  </CustomTooltipComponent>
);


const PermissionsTable = ({ onChange, activePermissions }) => {
  const { token, profile } = useContext(AuthContext);
  const [handlerPermissions, setHandlerPermissions] = useState([]);
  const [employeePermissions, setEmployeePermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const hasCustomLetters = Object.values(profile?.level ?? {}).some(
    features => Array.isArray(features) && features.includes('custom_letters')
  );

  const canViewLetters =
    profile?.customer_type === 'employee'
      ? hasCustomLetters
      : profile?.can_purchase === true
        ? true
        : hasCustomLetters;
        
  const getDescription = (permission) =>
    DESCRIPTIONS_BY_CODENAME[permission?.codename] || null;


  useEffect(() => {
    const fetchPermissions = async () => {
      setLoading(true);
      try {
        const permissions = await getPermissions(token);

        const customLettersPermissions = [
          'hr.manage_custom_letters_all',
          'hr.manage_custom_letters_ou',
        ];

        const filteredPermissions = permissions.filter(permission =>
          canViewLetters ||
          !customLettersPermissions.includes(permission.codename)
        );

        const handlerPerms = filteredPermissions.filter(permission => permission.role === 'handler');

        const employeePerms = filteredPermissions.filter(permission => permission.role === 'employee');

        setHandlerPermissions(handlerPerms.map(permission => ({
          ...permission,
          isChecked: activePermissions.includes(permission.id),
        })));

        setEmployeePermissions(employeePerms.map(permission => ({
          ...permission,
          isChecked: activePermissions.includes(permission.id),
        })));
      } catch (error) {
        console.error("Errore durante il recupero dei permessi:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPermissions();
  }, [token, activePermissions, canViewLetters]);

  const handlePermissionToggle = (id, isChecked) => {
    onChange(id, isChecked);
  };

  if (loading) {
    return <Fetching />;
  }

  return (
    <div>
      {/* Sezione Gestori */}
      <fieldset>
        <legend className="text-base font-semibold text-indigo-800">GESTORE</legend>
        <div className="mt-4 divide-y divide-gray-200 border-b border-t border-gray-200">
          {handlerPermissions.length > 0 ? (
            handlerPermissions.map((permission) => {
              const desc = getDescription(permission);
              return (
                <div key={permission.id} className="relative flex items-center py-2">
                  <div className="min-w-0 flex-1 text-md font-normal text-gray-900 leading-6">
                    <div className="flex items-center gap-2">
                      {desc && <InfoDot title={desc} />}
                      <span>{permission.name}</span>
                    </div>
                  </div>
                  <div className="ml-3 flex h-6 items-center">
                    <input
                      id={`handler-permission-${permission.id}`}
                      type="checkbox"
                      className="h-5 w-5 rounded text-indigo-600 focus:ring-indigo-600 me-3"
                      checked={permission.isChecked || false}
                      onChange={(e) => handlePermissionToggle(permission.id, e.target.checked)}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-gray-500">Nessuna autorizzazione disponibile per i gestori</div>
          )}
        </div>
      </fieldset>

      {/* Sezione Dipendenti */}
      <fieldset className="mt-8">
        <legend className="text-base font-semibold text-indigo-800">DIPENDENTE</legend>
        <div className="mt-4 divide-y divide-gray-200 border-b border-t border-gray-200">
          {employeePermissions.length > 0 ? (
            employeePermissions.map((permission) => {
              const desc = getDescription(permission);
              return (
                <div key={permission.id} className="relative flex items-center py-2">
                  <div className="min-w-0 flex-1 text-md font-normal text-gray-900 leading-6">
                    <div className="flex items-center gap-2">
                      {desc && <InfoDot title={desc} />}
                      <span>{permission.name}</span>
                    </div>
                  </div>
                  <div className="ml-3 flex h-6 items-center">
                    <input
                      id={`employee-permission-${permission.id}`}
                      type="checkbox"
                      className="h-5 w-5 rounded text-indigo-600 focus:ring-indigo-600 me-3"
                      checked={permission.isChecked || false}
                      onChange={(e) => handlePermissionToggle(permission.id, e.target.checked)}
                    />
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-gray-500">Nessuna autorizzazione disponibile per i dipendenti</div>
          )}
        </div>
      </fieldset>
    </div>
  );
};

export default PermissionsTable;
